import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {Link, RouteComponentProps} from 'react-router-dom';
import {Button, ButtonGroup, Col, Label, Row} from 'reactstrap';
import {AvField, AvForm, AvGroup, AvInput} from 'availity-reactstrap-validation';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IRootState} from 'app/shared/reducers';
import {getHealthcareFacilitiesCompact} from 'app/entities/healthcare-facility/healthcare-facility.reducer';
import {createEntity, getEntity, reset, updateEntity} from './ambulance.reducer';
import {getAllByType as getCountries} from "app/entities/region/region.reducer";
import {getEntities as getVehicleTypes} from 'app/entities/vehicleType/vehicleType.reducer';
import {IRegion} from "app/shared/model/region.model";
import {IVehicleType} from 'app/shared/model/vehicleType.model';
import {hasAnyPermission} from 'app/shared/auth/permissions';
import VillageAvTypeahead from "app/shared/layout/menus/village-av-typeahead";
import { useRegionLabels } from 'app/shared/hooks/useRegionLabels';
import { RegionDescriptionType } from 'app/shared/model/enumerations/region-description-type.model';

const neighborHealthcareFacilitySectionStyle = {
  border: '5px solid #e0e0e0',
  padding: '1em',
  marginBottom: '1em'
};

const neighborHealthcareFacilityLabelStyle = {
  marginBottom: '0.75em',
  display: 'flex',
  justifyContent: 'space-between'
};

const neighborHealthcareFacilityEntryRowStyle = {
  display: 'flex',
  justifyContent: 'space-between'
};

const addNeighborHealthcareFacilityButtonStyle = {
  fontSize: 'smaller'
};

const neighborHealthcareFacilityRemoveButtonStyle = {
  width: '10em',
  margin: '0.3em 0.3em 0.3em 1em',
  paddingLeft: '1em',
  fontSize: 'smaller'
};

export interface IAmbulanceUpdateProps extends StateProps, DispatchProps, RouteComponentProps<{ id: string }> {
}

export const AmbulanceUpdate = (props: IAmbulanceUpdateProps) => {
  const [healthcareFacilityId, setHealthcareFacilityId] = useState('0');
  const [isNew, setIsNew] = useState(!props.match.params || !props.match.params.id);
  const [activeCountry, setActiveCountry] = useState<IRegion | undefined>({})
  const [ambulanceCountry, setAmbulanceCountry] = useState<IRegion | undefined>({})
  const { getLabel } = useRegionLabels();
  const [healthcareFacilitiesByCountry, setHealthcareFacilitiesByCountry] = useState<Array<{short: string; full: string; id: string}>>([])

  const [activeStatus, setActiveStatus] = useState(true);
  const {ambulanceEntity, healthcareFacilities, loading, updating, vehicleTypes} = props;
  const [vehicleTypeList, setVehicleTypeList] = useState<IVehicleType[]>([]);
  const [neighborHfIds, setNeighborHfIds] = useState<Array<string | undefined>>([]);
  const [canAddNeighborHf, setCanAddNeighborHf] = useState(true);
  // Tracks the last countryId for which we've already fetched compact HFs, to avoid duplicate calls
  const fetchedHfCountryRef = useRef<string | undefined>(undefined);

  const filterHealthcareFacilityByCountry = (countryId?: string) => {
    if (!countryId) {
      setHealthcareFacilitiesByCountry([]);
      return;
    }
    const healthcareFacilities1 = props.healthcareFacilities.filter(hf => hf?.countryId === countryId);
    const healthcareFacNames = healthcareFacilities1.map((item: any) => {
      const council = item.councilName || '';
      const district = item.districtName || '';
      const fullParts = [item.name, council, district].filter(Boolean);
      return {
        short: item.name,
        full: fullParts.join(' / '),
        id: item.id
      };
    });
    setHealthcareFacilitiesByCountry(healthcareFacNames);
  };
  const handleClose = () => {
    props.history.push('/ambulance' + props.location.search);
  };

  const onCountryChange = (event) => {
    const countryId = event.target.value;
    const newActiveCountry = props.countries.find(country => country.id === countryId);
    setActiveCountry(newActiveCountry);
    // Always fetch on explicit user country change
    fetchedHfCountryRef.current = countryId;
    props.getHealthcareFacilitiesCompact(countryId);
    const filteredTypes = vehicleTypes.filter((item: any) => item.countryId === countryId);
    setVehicleTypeList(filteredTypes);
  };
  useEffect(() => {
    if (isNew) {
      props.reset();
    } else {
      props.getEntity(props.match.params.id);
    }

    props.getCountries();
    props.getVehicleTypes();
  }, []);

  useEffect(() => {
    if (isNew) {
      const ac = props.countries.find(country => country.id === (props.isAdmin ? props.countries[0].id : props.userCountryId));
      setActiveCountry(ac);
      if (ac?.id && fetchedHfCountryRef.current !== ac.id) {
        fetchedHfCountryRef.current = ac.id;
        props.getHealthcareFacilitiesCompact(ac.id);
      }
    }
  }, [props.countries]);

  useEffect(() => {
    if (vehicleTypes?.length > 0) {
      const filteredTypes = vehicleTypes.filter((item: any) => item.countryId === activeCountry?.id);
      setVehicleTypeList(filteredTypes);
    }
  }, [vehicleTypes, activeCountry]);

  useEffect(() => {
    filterHealthcareFacilityByCountry(activeCountry?.id);
  }, [props.healthcareFacilities, activeCountry]);

  useEffect(() => {
    if (!ambulanceEntity?.id) return;
    const ambulanceStatus = ambulanceEntity?.active;
    setActiveStatus(ambulanceStatus ?? true);
    setNeighborHfIds(ambulanceEntity?.neighborHealthcareFacilityIds ? [...ambulanceEntity.neighborHealthcareFacilityIds] : []);
    const ambCountryId = ambulanceEntity.vehicleType?.countryId;
    const ambCountry = props.countries.find(c => c.id === ambCountryId);
    if (ambCountry) {
      setActiveCountry(ambCountry);
      setAmbulanceCountry(ambCountry);
      // Only fetch if we haven't already fetched for this country
      if (fetchedHfCountryRef.current !== ambCountry.id) {
        fetchedHfCountryRef.current = ambCountry.id;
        props.getHealthcareFacilitiesCompact(ambCountry.id);
      }
    }
  }, [props.ambulanceEntity, props.countries]);

  useEffect(() => {
    if (props.updateSuccess) {
      handleClose();
    }
  }, [props.updateSuccess]);

  useEffect(() => {
    setCanAddNeighborHf((neighborHfIds.length === 0) || Boolean(neighborHfIds[neighborHfIds.length - 1]));
  }, [neighborHfIds]);

  const saveEntity = (event, errors, values) => {
    if (errors.length === 0) {
      const entity = {
        ...ambulanceEntity,
        ...values,
        active: activeStatus,
        neighborHealthcareFacilityIds: neighborHfIds,
        vehicleType: vehicleTypes.find(item => item.id === values.vehicleType),
        driverType: "CAR"
      };

      if (isNew) {
        props.createEntity(entity);
      } else {
        props.updateEntity(entity);
      }
    }
  };

  const healthCareFacilityList = (includeEmpty = false): Array<{short: string; full: string; id?: string}> => {
    let result: Array<{short: string; full: string; id?: string}> = includeEmpty ? ([{short: '', full: '', id: undefined}]) : []
    result = result.concat(healthcareFacilitiesByCountry);
    return result
  }

  const getHealthcareFacilityName = (id?: string) => {
    const hfName = healthcareFacilitiesByCountry?.find(item => item.id === id);
    return hfName?.full
  }

  const addNeighborHf = () => {
    setNeighborHfIds(neighborHfIds.concat(undefined));
  }

  const removeNeighborHf = (i) => {
    const newNeighborHfIds = neighborHfIds.slice();
    newNeighborHfIds.splice(i, 1);
    setNeighborHfIds(newNeighborHfIds);
  }

  const neighborHfChange = (changedIndex, newValue) => {
    const newNeighborHfIds = neighborHfIds.map((val, index) => {
      return (index === changedIndex) ? newValue : val;
    });
    setNeighborHfIds(newNeighborHfIds);
  }

  const neighborHfFields = () => {
    const result: JSX.Element[] = [];
    neighborHfIds.forEach((hfId, i) => {
      result.push(
        <div style={neighborHealthcareFacilityEntryRowStyle}>
          <div style={{flex: '1'}}>
            <VillageAvTypeahead
              onChange={(value) => neighborHfChange(i, value)}
              labelKey="name"
              valueKey="id"
              data={healthCareFacilityList(!hfId)}
              placeholder={!hfId ? '' : getHealthcareFacilityName(hfId)}
              id={'neighbor-healthcare-facility-' + i + '-typeahead'}
              loading={false}
              value={hfId || ''}
              input={{
                id: 'neighbor-healthcare-facility-' + i,
                name: 'neighborHealthcareFacilityValues[' + i + ']',
                required: true
              }}
            />
          </div>
          <Button style={neighborHealthcareFacilityRemoveButtonStyle} color="danger" id={`remove-neighbor-healthcare-facility-${i}`}
                  onClick={() => removeNeighborHf(i)}>
            <FontAwesomeIcon icon="trash"/>
            &nbsp; Remove
          </Button>
        </div>
      );
    });
    return result;
  }

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="mmamaApp.ambulance.home.createOrEditLabel">Create or edit an Ambulance</h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <AvForm model={isNew ? {} : ambulanceEntity} onSubmit={saveEntity}>
              {!isNew ? (
                <AvGroup>
                  <Label for="ambulance-id">ID</Label>
                  <AvInput id="ambulance-id" type="text" className="form-control" name="id" required readOnly/>
                </AvGroup>
              ) : null}
              <AvGroup>
                <Label id="lastNameLabel" for="driver-lastName">
                  Active Status
                </Label>
                <ButtonGroup style={{marginLeft: "30px"}}>

                  <Button
                    color={activeStatus ? 'primary' : 'secondary'}
                    value={activeStatus}
                    onClick={(e) => {
                      setActiveStatus(true)
                    }}
                  >
                    Active
                  </Button>


                  <Button
                    color={!activeStatus ? 'primary' : 'secondary'}
                    value={activeStatus}
                    onClick={() => {
                      setActiveStatus(false)
                    }}
                  >
                    Inactive
                  </Button>

                </ButtonGroup>
              </AvGroup>

              {props.countries && props.countries.length > 0 && (
                <AvGroup>
                  <Label for="hf-country">Country</Label>
                  <AvInput id="hf-country" type="select" className="form-control" name="country"
                           disabled={isNew ? false : true}
                           value={isNew ? activeCountry?.id : ambulanceCountry?.id} onChange={onCountryChange}>
                    {props.countries
                      && (props.isAdmin ? props.countries.map(country => (
                        <option value={country.id} key={country.id}>
                          {country.name}
                        </option>
                      )) : props.countries.map(country => (
                        props.userCountryId === country.id && <option value={country.id} key={country.id}>
                          {country.name}
                        </option>
                      )))}
                  </AvInput>
                </AvGroup>
              )
              }

              <AvGroup>
                <Label id="driverFirstNameLabel" for="ambulance-driverFirstName">
                  Driver First Name
                </Label>
                <AvField
                  id="ambulance-driverFirstName"
                  type="text"
                  name="driverFirstName"
                  validate={{
                    required: {value: true, errorMessage: 'This field is required.'},
                  }}
                />
              </AvGroup>
              <AvGroup>
                <Label id="driverLastNameLabel" for="ambulance-driverLastName">
                  Driver Last Name
                </Label>
                <AvField
                  id="ambulance-driverLastName"
                  type="text"
                  name="driverLastName"
                  validate={{
                    required: {value: true, errorMessage: 'This field is required.'},
                  }}
                />
              </AvGroup>
              <AvGroup>
                <Label id="vehicleTypeLabel" for="ambulance-vehicleType">
                  Vehicle Type
                </Label>
                <AvInput
                  id="ambulance-vehicleType"
                  type="select"
                  className="form-control"
                  name="vehicleType"
                  value={(!isNew && ambulanceEntity.vehicleType?.id) || vehicleTypeList[0]?.id}
                >
                  {vehicleTypeList.map((item: any, index) => (
                    <option value={item.id} key={index}>{item.enType}</option>
                  ))}
                </AvInput>
              </AvGroup>
              <AvGroup>
                <Label id="phoneNumberLabel" for="ambulance-phoneNumber">
                  Phone Number
                </Label>
                <AvField
                  id="ambulance-phoneNumber"
                  type="text"
                  name="phoneNumber"
                  validate={{
                    required: {value: true, errorMessage: 'This field is required.'},
                    maxLength: { value: 15, errorMessage: 'Maximum length is 15 characters' }
                  }}
                />
              </AvGroup>

              <AvGroup>
                <Label id="ownerphone_number_2" for="driver-ownerPhoneNumber">
                  Phone Number 2
                </Label>
                <AvField
                  id="ambulance-phone_number_2"
                  type="text"
                  name="phone_number_2"
                  validate={{maxLength: { value: 15, errorMessage: 'Maximum length is 15 characters' }}}
                />
              </AvGroup>

              <AvGroup>
                <Label id="ownerphone_number_3" for="driver-ownerPhoneNumber">
                  Phone Number 3
                </Label>
                <AvField
                  id="ambulance-phone_number_3"
                  type="text"
                  name="phone_number_3"
                  validate={{maxLength: { value: 15, errorMessage: 'Maximum length is 15 characters' }}}
                />
              </AvGroup>

              <AvGroup>
                <Label for="ambulance-healthcareFacility">Healthcare Facility</Label>
                {/* <AvInput id="ambulance-healthcareFacility" type="select" className="form-control"
                         name="healthcareFacilityId">
                  <option value="" key="0"/>
                  {healthcareFacilitiesByCountry
                    ? healthcareFacilitiesByCountry.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.name}>
                        {otherEntity.name}
                      </option>
                    ))
                    : null}
                </AvInput> */}
                <VillageAvTypeahead
                  labelKey="name"
                  valueKey="id"
                  data={healthCareFacilityList(false)}
                  placeholder={isNew ? "Healthcare Facility" : getHealthcareFacilityName(ambulanceEntity?.healthcareFacilityId)}
                  id="ambulance-healthcareFacility"
                  loading={false}
                  input={{
                    id: "ambulance-healthcareFacility",
                    name: "healthcareFacilityId",
                    required: true
                  }}
                />
              </AvGroup>
              <div style={neighborHealthcareFacilitySectionStyle}>
                <div style={neighborHealthcareFacilityLabelStyle}>
                  <span>Neighboring Healthcare Facilities</span>
                  <Button color="primary" id="add-neighbor-healthcare-facility" style={addNeighborHealthcareFacilityButtonStyle}
                          onClick={() => addNeighborHf()}
                          disabled={!canAddNeighborHf}>
                    <FontAwesomeIcon icon="plus"/>
                    &nbsp; Add Neighbor Healthcare Facility
                  </Button>
                </div>
                {neighborHfFields()}
              </div>
              <AvGroup>
                <Label id="mpesaNumberLabel" for="driver-mpesaNumber">
                  Mobile Money ID
                </Label>
                <AvField
                  id="driver-mpesaNumber"
                  type="text"
                  name="mpesaNumber"
                  validate={{
                    maxLength: { value: 10, errorMessage: 'Maximum length is 10 digits' },
                    number: {value: true, errorMessage: 'This field should be a number.'},
                  }}
                />
              </AvGroup>
              <Button tag={Link} id="cancel-save" to="/ambulance" replace color="info">
                <FontAwesomeIcon icon="arrow-left"/>
                &nbsp;
                <span className="d-none d-md-inline">Back</span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save"/>
                &nbsp; Save
              </Button>
            </AvForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  healthcareFacilities: storeState.healthcareFacility.compactEntities,
  ambulanceEntity: storeState.ambulance.entity,
  loading: storeState.ambulance.loading,
  updating: storeState.ambulance.updating,
  updateSuccess: storeState.ambulance.updateSuccess,
  countries: storeState.region.countries,
  userCountryId: storeState.authentication.userCountryId,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN']),
  vehicleTypes: storeState.vehicleType.entities
});

const mapDispatchToProps = {
  getHealthcareFacilitiesCompact,
  getEntity,
  updateEntity,
  createEntity,
  reset,
  getCountries,
  getVehicleTypes
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(AmbulanceUpdate);
